/**
 * DecisionFlow — ChangeManagementPanel
 * ui/src/components/admin/ChangeManagementPanel.jsx
 * S621 — COMPLETE REBUILD + CLEANUP
 * S622 — OI-CCM-QUOTA-ENTITY-ID: EntitySelector for quota/credit now resolves
 *         actual quota_id/credit_id via GET /quotas?position_id=... before
 *         passing entityId to AmendmentBeginModal. Fixes ENTITY_NOT_FOUND 404.
 *         OI-CCM-RATE-TABLE-ENDPOINT: Removed unsupported ?is_active=true&limit=200
 *         query params from /rate-tables and /plan-components fetch URLs.
 *
 * Four-tab Change Management surface.
 * Tab 1 — Pending Approval  (CA-2 action queue)
 * Tab 2 — Open              (begun, not yet submitted)
 * Tab 3 — Committed         (impact review queue)
 * Tab 4 — History           (full audit log, filterable, read-only)
 *
 * Route: /admin/change-management
 * Props: period, fiscalPeriodId, wizardSessionId
 *
 * Spec authority: comp_change_management_spec_v1_0.md §7–8
 * AmendmentBeginModal: read from source — props confirmed:
 *   open, entityType, entityId, fiscalPeriodId, wizardSessionId,
 *   onBegin, onCancel, packageMode
 * Import path: "./AmendmentBeginModal.jsx" — resolves from components/admin/
 *
 * BUG FIXES vs S620:
 *   1. Role Transition: navigate() stub removed — RoleTransitionInitiator
 *      rendered inline in panel body (§8.8)
 *   2. Entity selector pre-step added before AmendmentBeginModal for
 *      quota/credit/rate_table/plan_component (entityId was always null)
 *   3. Change package reject: inline rejection reason textarea added
 *      (was hardcoded string in S620)
 *
 * PERMANENT RULES:
 *   - createPortal(…, document.body) at z-[9999] — ALL modals
 *   - CSS: rgb(var(--color-*)) tokens only
 *   - No <form> tags — onClick/onChange only
 *   - No new npm dependencies
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../auth/AuthContext.jsx";
import {
  GitPullRequest, Plus, CheckCircle, X, AlertTriangle,
  Clock, ChevronDown, ChevronRight, Loader2, Search,
  ArrowRight, RotateCcw, User, Calendar, FileText,
} from "lucide-react";
import AmendmentBeginModal from "./AmendmentBeginModal.jsx";

const API = "/api/v1";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    const msg  = body?.detail?.error?.message ?? body?.detail ?? `HTTP ${r.status}`;
    throw Object.assign(new Error(msg), { code: body?.detail?.error?.code, status: r.status });
  }
  return r.json();
}

function useFetch(url) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [tick, setTick]       = useState(0);
  const refresh = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    fetch(url, { credentials: "include" })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { if (!cancelled) { setData(d); setError(null); } })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [url, tick]);

  return { data, loading, error, refresh };
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days  = Math.floor(hours / 24);
  if (days > 0)  return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0)  return `${mins}m ago`;
  return "just now";
}

function countdown(iso) {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Expired";
  const hours = Math.floor(diff / 3600000);
  const mins  = Math.floor((diff % 3600000) / 60000);
  if (hours > 0) return `in ${hours}h ${mins}m`;
  return `in ${mins}m`;
}

function StatusPill({ status }) {
  const tones = {
    open:             "bg-[rgb(var(--color-text-muted))]/10 text-[rgb(var(--color-text-muted))]",
    pending_approval: "bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]",
    approved:         "bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]",
    committed:        "bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]",
    rejected:         "bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]",
    impact_rejected:  "bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]",
    reverted:         "bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]",
    expired:          "bg-[rgb(var(--color-text-muted))]/10 text-[rgb(var(--color-text-muted))]",
    cancelled:        "bg-[rgb(var(--color-text-muted))]/10 text-[rgb(var(--color-text-muted))]",
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-medium ${tones[status] ?? tones.open}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function LoadingRows({ n = 3 }) {
  return (
    <div className="space-y-3">
      {[...Array(n)].map((_, i) => (
        <div key={i} className="rounded-xl border border-[rgb(var(--color-border))] p-4 animate-pulse">
          <div className="h-4 w-1/3 rounded bg-[rgb(var(--color-surface-muted))] mb-2" />
          <div className="h-3 w-2/3 rounded bg-[rgb(var(--color-surface-muted))]" />
        </div>
      ))}
    </div>
  );
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
      {msg}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AmendmentCard — shared across tabs
// ---------------------------------------------------------------------------
function AmendmentCard({ amd, currentUserId, onApprove, onReject, onSubmit, onCancel, showActions, tab }) {
  const [expanded,           setExpanded]           = useState(false);
  const [rejectOpen,         setRejectOpen]         = useState(false);
  const [rejReason,          setRejReason]          = useState("");
  const [acting,             setActing]             = useState(false);
  const [actionError,        setActionError]        = useState(null);
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);

  const isSelf = amd.initiated_by && currentUserId && amd.initiated_by.toLowerCase() === currentUserId.toLowerCase();

  async function doAction(actionFn, body = {}) {
    setActing(true);
    setActionError(null);
    try {
      await actionFn(amd.amendment_id, body);
      setRejectOpen(false);
      setRejReason("");
    } catch (e) {
      setActionError(e.message);
    } finally {
      setActing(false);
    }
  }

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
      <div className="flex items-start justify-between p-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              {amd.amended_entity_type?.replace(/_/g, " ")} · {amd.amendment_reason_code?.replace(/_/g, " ")}
            </span>
            <StatusPill status={amd.amendment_status} />
          </div>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            ID: {String(amd.amendment_id).slice(0, 8)}…{" "}
            · {timeAgo(amd.initiated_at)}{" "}
            · {isSelf ? "by You" : (amd.initiated_by_name ?? amd.initiated_by?.slice(0, 8))}
          </p>
          {tab === "open" && amd.expired_at && (
            <p className="text-tok-xs text-[rgb(var(--color-warning))] mt-0.5">
              Expires {countdown(amd.expired_at)}
            </p>
          )}
          {amd.amendment_reason_note && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5 italic">
              "{amd.amendment_reason_note}"
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 ml-3 shrink-0 flex-wrap justify-end">
          {/* Pending tab actions */}
          {showActions && tab === "pending" && (
            isSelf ? (
              <>
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))] italic max-w-[160px] text-right">
                  You initiated this — another comp admin must approve.
                </span>
                {/* S622: initiator can withdraw a pending_approval amendment */}
                <button
                  type="button"
                  onClick={() => setWithdrawConfirmOpen(true)}
                  disabled={acting}
                  className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-warning))] hover:bg-[rgb(var(--color-warning))]/20 disabled:opacity-50"
                >
                  {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Withdraw"}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => doAction(onApprove)}
                  disabled={acting}
                  className="rounded-lg bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-success))] hover:bg-[rgb(var(--color-success))]/20 disabled:opacity-50"
                >
                  {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Approve"}
                </button>
                <button
                  type="button"
                  onClick={() => setRejectOpen(v => !v)}
                  disabled={acting}
                  className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/20"
                >
                  Reject
                </button>
              </>
            )
          )}

          {/* Open tab actions — any comp_admin can submit; only initiator can cancel */}
          {showActions && tab === "open" && (
            <>
              <button
                type="button"
                onClick={() => doAction(onSubmit)}
                disabled={acting}
                className="rounded-lg bg-[rgb(var(--color-primary))]/10 border border-[rgb(var(--color-primary))]/30 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary))]/20 disabled:opacity-50"
              >
                {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Submit for Approval"}
              </button>
              {isSelf && (
                <button
                  type="button"
                  onClick={() => doAction(onCancel)}
                  disabled={acting}
                  className="rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1 text-tok-xs text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
                >
                  Cancel
                </button>
              )}
            </>
          )}

          {/* Expand/collapse toggle */}
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="rounded-lg border border-[rgb(var(--color-border))] p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Action error */}
      {actionError && (
        <div className="px-4 pb-3">
          <ErrorMsg msg={actionError} />
        </div>
      )}

      {/* Inline reject form */}
      {rejectOpen && (
        <div className="border-t border-[rgb(var(--color-border))] p-4 bg-[rgb(var(--color-surface-muted))]">
          <p className="text-tok-xs font-medium text-[rgb(var(--color-text))] mb-2">Rejection reason (required)</p>
          <textarea
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none resize-none"
            rows={2}
            value={rejReason}
            onChange={e => setRejReason(e.target.value)}
            placeholder="Explain why this amendment is being rejected…"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={() => { setRejectOpen(false); setRejReason(""); }}
              className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1 text-tok-xs text-[rgb(var(--color-text-muted))]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!rejReason.trim() || acting}
              onClick={() => doAction(onReject, { rejection_reason: rejReason })}
              className="rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1 text-tok-xs font-medium text-white disabled:opacity-50"
            >
              {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm Reject"}
            </button>
          </div>
        </div>
      )}

      {/* Expanded detail — before/after state diff */}
      {expanded && (
        <div className="border-t border-[rgb(var(--color-border))] p-4 bg-[rgb(var(--color-surface-muted))] space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] mb-1">Before State</p>
              <pre className="text-[10px] text-[rgb(var(--color-text))] whitespace-pre-wrap break-all bg-[rgb(var(--color-surface))] rounded-lg p-2 border border-[rgb(var(--color-border))] max-h-32 overflow-auto">
                {amd.before_state
                  ? JSON.stringify(typeof amd.before_state === "string" ? JSON.parse(amd.before_state) : amd.before_state, null, 2)
                  : "—"}
              </pre>
            </div>
            <div>
              <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] mb-1">After State</p>
              <pre className="text-[10px] text-[rgb(var(--color-text))] whitespace-pre-wrap break-all bg-[rgb(var(--color-surface))] rounded-lg p-2 border border-[rgb(var(--color-border))] max-h-32 overflow-auto">
                {amd.after_state
                  ? JSON.stringify(typeof amd.after_state === "string" ? JSON.parse(amd.after_state) : amd.after_state, null, 2)
                  : "Not yet committed"}
              </pre>
            </div>
          </div>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Reason: {amd.amendment_reason_code?.replace(/_/g, " ")}
            {" · "}Initiated: {amd.initiated_at ? new Date(amd.initiated_at).toLocaleString() : "—"}
            {amd.impacted_stages?.length > 0 && ` · Impacted stages: ${amd.impacted_stages.join(", ")}`}
          </p>
        </div>
      )}

      {/* OI-CCM-WITHDRAW-CONFIRM-MODAL — portal at z-[9999], fires cancel only on Confirm */}
      {withdrawConfirmOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-[rgb(var(--color-overlay))]/60"
            onClick={() => setWithdrawConfirmOpen(false)}
          />
          {/* Dialog */}
          <div className="relative z-10 w-full max-w-sm rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 shadow-xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--color-warning))]/10">
                <AlertTriangle className="h-4 w-4 text-[rgb(var(--color-warning))]" />
              </div>
              <div>
                <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Withdraw Amendment</h2>
                <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
                  Are you sure you want to withdraw this amendment? This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setWithdrawConfirmOpen(false)}
                disabled={acting}
                className="rounded-lg border border-[rgb(var(--color-border))] px-4 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setWithdrawConfirmOpen(false); doAction(onCancel); }}
                disabled={acting}
                className="rounded-lg bg-[rgb(var(--color-warning))] px-4 py-1.5 text-tok-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Withdraw"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChangePackageCard — for Pending Approval tab
// ---------------------------------------------------------------------------
function ChangePackageCard({ pkg, currentUserId, onApprove, onReject }) {
  const [rejectOpen,  setRejectOpen]  = useState(false);
  const [rejReason,   setRejReason]   = useState("");
  const [acting,      setActing]      = useState(false);
  const [actionError, setActionError] = useState(null);
  const [expanded,    setExpanded]    = useState(false);

  const isSelf = pkg.initiated_by && currentUserId && pkg.initiated_by.toLowerCase() === currentUserId.toLowerCase();

  async function doApprove() {
    setActing(true);
    setActionError(null);
    try {
      await onApprove(pkg.change_package_id);
    } catch (e) {
      setActionError(e.message);
    } finally {
      setActing(false);
    }
  }

  async function doReject() {
    if (!rejReason.trim()) return;
    setActing(true);
    setActionError(null);
    try {
      await onReject(pkg.change_package_id, { rejection_reason: rejReason });
      setRejectOpen(false);
      setRejReason("");
    } catch (e) {
      setActionError(e.message);
    } finally {
      setActing(false);
    }
  }

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
      <div className="flex items-start justify-between p-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              Change Package · {pkg.package_reason_code?.replace(/_/g, " ")}
            </span>
            <StatusPill status={pkg.package_status} />
          </div>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {timeAgo(pkg.initiated_at)} · {pkg.entity_summary?.length ?? 0} entities
            {pkg.initiated_by_name && ` · by ${isSelf ? "You" : pkg.initiated_by_name}`}
          </p>
          {pkg.package_reason_note && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] italic mt-0.5">
              "{pkg.package_reason_note}"
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 ml-3 shrink-0 flex-wrap justify-end">
          {isSelf ? (
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))] italic max-w-[160px] text-right">
              You initiated this — another comp admin must approve.
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={doApprove}
                disabled={acting}
                className="rounded-lg bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-success))] hover:bg-[rgb(var(--color-success))]/20 disabled:opacity-50"
              >
                {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Approve"}
              </button>
              <button
                type="button"
                onClick={() => setRejectOpen(v => !v)}
                disabled={acting}
                className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/20"
              >
                Reject
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="rounded-lg border border-[rgb(var(--color-border))] p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {actionError && (
        <div className="px-4 pb-3"><ErrorMsg msg={actionError} /></div>
      )}

      {/* Inline reject form — fix for Bug 3 */}
      {rejectOpen && (
        <div className="border-t border-[rgb(var(--color-border))] p-4 bg-[rgb(var(--color-surface-muted))]">
          <p className="text-tok-xs font-medium text-[rgb(var(--color-text))] mb-2">Rejection reason (required)</p>
          <textarea
            className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none resize-none"
            rows={2}
            value={rejReason}
            onChange={e => setRejReason(e.target.value)}
            placeholder="Explain why this change package is being rejected…"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={() => { setRejectOpen(false); setRejReason(""); }}
              className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1 text-tok-xs text-[rgb(var(--color-text-muted))]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!rejReason.trim() || acting}
              onClick={doReject}
              className="rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1 text-tok-xs font-medium text-white disabled:opacity-50"
            >
              {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm Reject"}
            </button>
          </div>
        </div>
      )}

      {/* Entity summary on expand */}
      {expanded && pkg.entity_summary?.length > 0 && (
        <div className="border-t border-[rgb(var(--color-border))] p-4 bg-[rgb(var(--color-surface-muted))]">
          <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide mb-2">Entities in package</p>
          <div className="space-y-1">
            {pkg.entity_summary.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-tok-xs text-[rgb(var(--color-text))]">
                <span className="rounded bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-1.5 py-0.5 font-mono text-[10px] text-[rgb(var(--color-text-muted))]">
                  {e.amended_entity_type}
                </span>
                <span className="text-[rgb(var(--color-text-muted))]">{e.amended_entity_id?.slice(0, 8)}…</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — Pending Approval
// ---------------------------------------------------------------------------
function PendingApprovalTab({ fiscalPeriodId, currentUserId, onRefresh }) {
  const amendUrl = `${API}/amendments?amendment_status=pending_approval${fiscalPeriodId ? `&fiscal_period_id=${fiscalPeriodId}` : ""}&limit=50`;
  const packageUrl = `${API}/amendments/packages?package_status=pending_approval${fiscalPeriodId ? `&fiscal_period_id=${fiscalPeriodId}` : ""}&limit=50`;

  const { data: amdData,  loading: amdLoading,  refresh: refreshAmds } = useFetch(amendUrl);
  const { data: pkgData,  loading: pkgLoading,  refresh: refreshPkgs } = useFetch(packageUrl);

  const amendments = amdData?.amendments ?? [];
  const packages   = pkgData?.packages   ?? [];
  const total      = amendments.length + packages.length;

  async function handleApprove(amendmentId) {
    await apiFetch(`/amendments/${amendmentId}/approve`, { method: "POST" });
    refreshAmds(); onRefresh?.();
  }
  async function handleReject(amendmentId, body) {
    await apiFetch(`/amendments/${amendmentId}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    refreshAmds(); onRefresh?.();
  }
  async function handleCancel(amendmentId) {
    await apiFetch(`/amendments/${amendmentId}/cancel`, { method: "POST" });
    refreshAmds(); onRefresh?.();
  }
  async function handleApprovePackage(packageId) {
    await apiFetch(`/amendments/packages/${packageId}/approve`, { method: "POST" });
    refreshPkgs(); onRefresh?.();
  }
  async function handleRejectPackage(packageId, body) {
    await apiFetch(`/amendments/packages/${packageId}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    refreshPkgs(); onRefresh?.();
  }

  if (amdLoading || pkgLoading) return <LoadingRows />;

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <CheckCircle className="h-10 w-10 text-[rgb(var(--color-success))] mb-3" />
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No pending approvals</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-1">All amendments are up to date.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        {total} item{total !== 1 ? "s" : ""} awaiting action
      </p>

      {packages.length > 0 && (
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] mb-2">
            Change Packages ({packages.length})
          </p>
          <div className="space-y-2">
            {packages.map(pkg => (
              <ChangePackageCard
                key={pkg.change_package_id}
                pkg={pkg}
                currentUserId={currentUserId}
                onApprove={handleApprovePackage}
                onReject={handleRejectPackage}
              />
            ))}
          </div>
        </div>
      )}

      {amendments.length > 0 && (
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] mb-2">
            Single Amendments ({amendments.length})
          </p>
          <div className="space-y-2">
            {amendments.map(amd => (
              <AmendmentCard
                key={amd.amendment_id}
                amd={amd}
                currentUserId={currentUserId}
                onApprove={handleApprove}
                onReject={handleReject}
                onCancel={handleCancel}
                showActions
                tab="pending"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Open
// ---------------------------------------------------------------------------
function OpenTab({ fiscalPeriodId, currentUserId, onRefresh }) {
  const url = `${API}/amendments?amendment_status=open${fiscalPeriodId ? `&fiscal_period_id=${fiscalPeriodId}` : ""}&limit=50`;
  const { data, loading, refresh } = useFetch(url);
  const amendments = data?.amendments ?? [];

  async function handleSubmit(amendmentId) {
    await apiFetch(`/amendments/${amendmentId}/submit`, { method: "POST" });
    refresh(); onRefresh?.();
  }
  async function handleCancel(amendmentId) {
    await apiFetch(`/amendments/${amendmentId}/cancel`, { method: "POST" });
    refresh(); onRefresh?.();
  }

  if (loading) return <LoadingRows />;

  if (amendments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Clock className="h-10 w-10 text-[rgb(var(--color-text-muted))] mb-3" />
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No open amendments</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-1">
          Amendments you begin will appear here before submission.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        {amendments.length} open amendment{amendments.length !== 1 ? "s" : ""}
      </p>
      {amendments.map(amd => (
        <AmendmentCard
          key={amd.amendment_id}
          amd={amd}
          currentUserId={currentUserId}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          showActions
          tab="open"
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — Committed (impact review queue)
// ---------------------------------------------------------------------------
function CommittedTab({ fiscalPeriodId, currentUserId, wizardSessionId, onRefresh }) {
  const url = `${API}/amendments?amendment_status=committed${fiscalPeriodId ? `&fiscal_period_id=${fiscalPeriodId}` : ""}&limit=50`;
  const { data, loading, refresh } = useFetch(url);
  const amendments = data?.amendments ?? [];

  const [triggeringPreview, setTriggeringPreview] = useState(false);
  const [previewStatus,     setPreviewStatus]     = useState(null); // null | "running" | "done" | "failed" | "timeout"
  const [rejectingImpact,   setRejectingImpact]   = useState(null); // amendment_id
  const [rejReason,         setRejReason]         = useState("");
  const [acting,            setActing]             = useState(false);
  const [actionError,       setActionError]        = useState(null);

  // Poll GET /calculation-runs/{calculationRunId} every 5s until terminal.
  // Terminal states: completed, completed_with_exclusions, failed, cancelled.
  // Max 60 attempts (5 minutes). On timeout: unlock button, show soft message.
  // Button stays locked (triggeringPreview=true) for the entire poll window.
  const POLL_INTERVAL_MS  = 5000;
  const POLL_MAX_ATTEMPTS = 60;
  const TERMINAL_STATUSES = new Set([
    "completed", "completed_with_exclusions", "failed", "cancelled",
  ]);

  async function pollUntilTerminal(calculationRunId) {
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      try {
        const run = await apiFetch(`/calculation-runs/${calculationRunId}`);
        const status = run?.run_status ?? run?.runStatus;
        if (TERMINAL_STATUSES.has(status)) {
          return status;
        }
      } catch (e) {
        // Network blip — log and keep polling; don't abort on a single fetch failure
        log && log(`preview poll fetch error (attempt ${attempt + 1}): ${e.message}`);
      }
    }
    return "timeout";
  }

  async function handleTriggerPreview() {
    if (!fiscalPeriodId) return;
    setTriggeringPreview(true);
    setPreviewStatus("running");
    setActionError(null);
    try {
      // Enqueue preview run — returns 202 immediately with calculationRunId.
      // wizard_session_id no longer sent (retired S859 — worker uses
      // tenant_id + fiscal_period_id to stamp amendment_log).
      const enqueued = await apiFetch("/calculation-runs/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fiscal_period_id: fiscalPeriodId }),
      });

      const calculationRunId = enqueued?.calculationRunId ?? enqueued?.calculation_run_id;
      if (!calculationRunId) {
        throw new Error("Preview run enqueued but no calculationRunId returned.");
      }

      // Poll until terminal status or timeout
      const terminalStatus = await pollUntilTerminal(calculationRunId);

      if (terminalStatus === "timeout") {
        // Run is still executing — unlock UI, show soft message.
        // Worker will complete and stamp amendment_log; user will see
        // the update on next manual refresh.
        setPreviewStatus("timeout");
        setActionError(
          "The preview run is taking longer than expected. " +
          "It will complete in the background — refresh this page in a moment."
        );
      } else if (terminalStatus === "failed" || terminalStatus === "cancelled") {
        setPreviewStatus("failed");
        setActionError(
          "The preview run did not complete successfully. " +
          "Check the Calculations activity log for details."
        );
      } else {
        // completed or completed_with_exclusions — success
        setPreviewStatus("done");
        refresh(); // Re-fetch committed amendments — preview_run_id_after now set
      }
    } catch (e) {
      setPreviewStatus("failed");
      setActionError(e.message);
    } finally {
      setTriggeringPreview(false);
    }
  }

  async function handleRejectImpact(amendmentId) {
    if (!rejReason.trim()) return;
    setActing(true);
    setActionError(null);
    try {
      await apiFetch(`/amendments/${amendmentId}/reject-impact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rejection_reason: rejReason }),
      });
      setRejectingImpact(null);
      setRejReason("");
      refresh(); onRefresh?.();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setActing(false);
    }
  }

  if (loading) return <LoadingRows />;

  if (amendments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <CheckCircle className="h-10 w-10 text-[rgb(var(--color-success))] mb-3" />
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No committed amendments awaiting review</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-1">
          Committed amendments pending impact review will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        {amendments.length} item{amendments.length !== 1 ? "s" : ""} awaiting impact review
      </p>
      {actionError && <ErrorMsg msg={actionError} />}

      {amendments.map(amd => {
        const hasPreview       = !!amd.preview_run_id_after;
        const isRejectingThis  = rejectingImpact === amd.amendment_id;
        const canRejectImpact  = hasPreview && amd.initiated_by !== currentUserId;

        return (
          <div key={amd.amendment_id} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                    {amd.amended_entity_type?.replace(/_/g, " ")} · {amd.amendment_reason_code?.replace(/_/g, " ")}
                  </p>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
                    Committed {timeAgo(amd.committed_at)}
                  </p>
                  <p className="text-tok-xs mt-1">
                    Preview run:{" "}
                    {hasPreview
                      ? <span className="text-[rgb(var(--color-success))]">✓ Completed</span>
                      : <span className="text-[rgb(var(--color-text-muted))]">⏳ Not yet triggered</span>
                    }
                  </p>
                  {amd.impacted_stages?.length > 0 && (
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
                      Impacted stages requiring reapproval: {amd.impacted_stages.join(", ")}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-3 shrink-0 flex-wrap justify-end">
                  {!hasPreview && (
                    <button
                      type="button"
                      onClick={handleTriggerPreview}
                      disabled={triggeringPreview}
                      className="rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1 text-tok-xs text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50"
                    >
                      {triggeringPreview
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : "Trigger Preview Run"}
                    </button>
                  )}
                  {canRejectImpact && (
                    <button
                      type="button"
                      onClick={() => { setRejectingImpact(amd.amendment_id); setRejReason(""); setActionError(null); }}
                      className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/20"
                    >
                      Reject Impact
                    </button>
                  )}
                </div>
              </div>

              {!hasPreview && (
                <div className="mt-3 rounded-lg bg-[rgb(var(--color-surface-muted))] p-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Run a preview calculation to see payout impact before reapproving.
                </div>
              )}
            </div>

            {/* Reject impact inline form */}
            {isRejectingThis && (
              <div className="border-t border-[rgb(var(--color-border))] p-4 bg-[rgb(var(--color-surface-muted))]">
                <div className="rounded-lg border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/5 p-3 mb-3">
                  <div className="flex items-start gap-2 text-tok-xs text-[rgb(var(--color-warning))]">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <p>
                      This will revert the entity to its previous state using <code className="font-mono">before_state</code> and
                      require a new preview run to confirm the reverted state is clean.
                    </p>
                  </div>
                </div>
                <textarea
                  className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none resize-none"
                  rows={2}
                  value={rejReason}
                  onChange={e => setRejReason(e.target.value)}
                  placeholder="Rejection reason (required)"
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => { setRejectingImpact(null); setRejReason(""); }}
                    className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1 text-tok-xs text-[rgb(var(--color-text-muted))]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!rejReason.trim() || acting}
                    onClick={() => handleRejectImpact(amd.amendment_id)}
                    className="rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1 text-tok-xs font-medium text-white disabled:opacity-50"
                  >
                    {acting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Confirm Reject Impact"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 4 — History
// ---------------------------------------------------------------------------
const HISTORY_STATUSES    = ["open","pending_approval","approved","committed","rejected","impact_rejected","reverted","expired","cancelled"];
const HISTORY_ENTITY_TYPES = ["position","quota","credit","rate_table","plan_component","ramp_schedule","plan_assignment","exchange_rate","territory_assignment","overlay_config","mbo_definition","scenario_definition"];

function HistoryTab({ fiscalPeriodId }) {
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter,   setTypeFilter]   = useState("");
  const [search,       setSearch]       = useState("");
  const [page,         setPage]         = useState(1);
  const limit = 20;

  const params = new URLSearchParams({ limit: String(limit), offset: String((page - 1) * limit) });
  if (fiscalPeriodId) params.set("fiscal_period_id", String(fiscalPeriodId));
  if (statusFilter)   params.set("amendment_status", statusFilter);
  if (typeFilter)     params.set("amended_entity_type", typeFilter);

  const { data, loading } = useFetch(`${API}/amendments?${params}`);
  const amendments = data?.amendments ?? [];
  const total      = data?.total ?? amendments.length;

  const filtered = search
    ? amendments.filter(a =>
        a.amendment_reason_code?.includes(search) ||
        a.amended_entity_type?.includes(search)   ||
        a.amendment_id?.includes(search)
      )
    : amendments;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
          <input
            className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pl-7 pr-3 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none w-44"
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          {HISTORY_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select
          className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1.5 text-tok-xs text-[rgb(var(--color-text))] focus:outline-none"
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Entity Types</option>
          {HISTORY_ENTITY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      {loading ? <LoadingRows /> : (
        <>
          {filtered.length === 0 && (
            <div className="py-12 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
              No amendments found.
            </div>
          )}
          <div className="space-y-2">
            {filtered.map(amd => (
              <AmendmentCard
                key={amd.amendment_id}
                amd={amd}
                currentUserId={null}
                showActions={false}
                tab="history"
              />
            ))}
          </div>
          {total > limit && (
            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1 text-tok-xs disabled:opacity-30"
              >
                ← Prev
              </button>
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Page {page}</span>
              <button
                type="button"
                onClick={() => setPage(p => p + 1)}
                disabled={amendments.length < limit}
                className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1 text-tok-xs disabled:opacity-30"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entity Selector — pre-step before AmendmentBeginModal (fixes Bug 2)
// Fetches entities of the required type and lets user pick one to get a real entityId.
// ---------------------------------------------------------------------------
// OI-CCM-QUOTA-ENTITY-ID FIX (S622):
// begin_amendment does: SELECT * FROM quota WHERE quota_id = $1
// Passing position_id as amended_entity_id for entity_type "quota" always
// returns ENTITY_NOT_FOUND because quota_id ≠ position_id.
// Fix: for quota/credit, user picks a position, then we resolve the actual
// quota_id / credit_id via GET /quotas?position_id=...&fiscal_period_id=...
// before calling onSelect. The backend receives the real PK it expects.
//
// OI-CCM-RATE-TABLE-ENDPOINT (S622):
// GET /rate-tables accepts only role_id and segment_id — is_active/limit ignored.
// GET /plan-components accepts no query params — is_active/limit ignored.
// Removed unsupported params to keep URLs clean.
// ---------------------------------------------------------------------------
function EntitySelector({ entityType, fiscalPeriodId, onSelect, onCancel }) {
  const [search,    setSearch]    = useState("");
  const [selected,  setSelected]  = useState(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  // Endpoint and ID/label fields vary per entity type.
  // quota/credit: fetch positions for display, then resolve actual entity PK before onSelect.
  const config = {
    quota: {
      url:            `/positions?is_active=true&limit=500`,
      idField:        "position_id",
      label:          p => `${p.full_name} (${p.employee_id ?? p.position_id.slice(0, 8)})`,
      subLabel:       p => p.role_code ?? "",
      note:           "Select the position whose quota you want to correct.",
      resolveEntity:  true,   // must fetch quota_id from /quotas before onSelect
      entityPkField:  "quota_id",
      resolveUrl:     (posId) => `/quotas?position_id=${posId}&fiscal_period_id=${fiscalPeriodId}&limit=1`,
      resolveKey:     (data)  => (data?.data ?? data?.quotas ?? [])[0]?.quota_id ?? null,
    },
    credit: {
      url:            `/positions?is_active=true&limit=500`,
      idField:        "position_id",
      label:          p => `${p.full_name} (${p.employee_id ?? p.position_id.slice(0, 8)})`,
      subLabel:       p => p.role_code ?? "",
      note:           "Select the position whose credit entry you want to correct.",
      resolveEntity:  true,
      entityPkField:  "credit_id",
      resolveUrl:     (posId) => `/credits?position_id=${posId}&fiscal_period_id=${fiscalPeriodId}&limit=1`,
      resolveKey:     (data)  => (data?.data ?? data?.credits ?? [])[0]?.credit_id ?? null,
    },
    rate_table: {
      // GET /rate-tables supports role_id and segment_id filters only — no is_active/limit params
      url:            `/rate-tables`,
      idField:        "rate_table_id",
      label:          rt => `${rt.rate_table_name} (${rt.rate_table_code})`,
      subLabel:       rt => `role_id: ${rt.role_id} · segment_id: ${rt.segment_id}`,
      note:           "Select the rate table you want to adjust.",
      resolveEntity:  false,
    },
    plan_component: {
      // GET /plan-components accepts no query params
      url:            `/plan-components`,
      idField:        "plan_component_id",
      label:          pc => `Plan Component #${pc.plan_component_id}`,
      subLabel:       pc => `role_id: ${pc.role_id} · segment_id: ${pc.segment_id}`,
      note:           "Select the plan component you want to adjust.",
      resolveEntity:  false,
    },
  };

  const cfg = config[entityType];
  const url = cfg ? `${API}${cfg.url}` : null;
  const { data, loading } = useFetch(url);

  // Normalise response — different endpoints return different shapes
  const items = data?.data ?? data?.positions ?? data?.rate_tables ?? data?.plan_components ?? data?.items ?? [];

  const filtered = search
    ? items.filter(item => {
        const lbl = cfg?.label(item)?.toLowerCase() ?? "";
        return lbl.includes(search.toLowerCase());
      })
    : items;

  async function handleConfirm() {
    if (!selected) return;
    setResolveError(null);

    if (cfg.resolveEntity) {
      // quota/credit: must resolve actual PK before passing to AmendmentBeginModal
      setResolving(true);
      try {
        const posId = String(selected[cfg.idField]);
        const resolveData = await apiFetch(cfg.resolveUrl(posId));
        const resolvedId  = cfg.resolveKey(resolveData);
        if (!resolvedId) {
          setResolveError(
            `No ${entityType} record found for this position in the current period. ` +
            `Ensure a ${entityType} has been loaded before attempting an amendment.`
          );
          return;
        }
        onSelect({ entityId: String(resolvedId), entityType });
      } catch (err) {
        setResolveError(err.message ?? `Failed to resolve ${entityType} record.`);
      } finally {
        setResolving(false);
      }
      return;
    }

    // rate_table / plan_component — idField is already the real PK
    const entityId = String(selected[cfg.idField]);
    onSelect({ entityId, entityType });
  }

  if (!cfg) {
    // Fallback for "other" — just proceed with null entityId (will fail at modal level with a clear message)
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {cfg.note && (
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{cfg.note}</p>
      )}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
        <input
          className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pl-8 pr-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
          placeholder="Search…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="rounded-xl border border-[rgb(var(--color-border))] overflow-hidden max-h-64 overflow-y-auto">
        {loading ? (
          <div className="p-4 space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-4 rounded bg-[rgb(var(--color-surface-muted))] animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="p-4 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">No results found.</p>
        ) : (
          filtered.map((item, i) => {
            const itemId  = item[cfg.idField];
            const isSelected = selected && selected[cfg.idField] === itemId;
            return (
              <button
                key={itemId ?? i}
                type="button"
                onClick={() => setSelected(item)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-[rgb(var(--color-border))] last:border-0 transition-colors ${
                  isSelected
                    ? "bg-[rgb(var(--color-primary))]/5 border-l-2 border-l-[rgb(var(--color-primary))]"
                    : "hover:bg-[rgb(var(--color-surface-muted))]"
                }`}
              >
                <span className={`h-3.5 w-3.5 shrink-0 rounded-full border-2 flex items-center justify-center ${
                  isSelected ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]" : "border-[rgb(var(--color-text-muted))]"
                }`}>
                  {isSelected && <span className="h-1 w-1 rounded-full bg-white" />}
                </span>
                <div className="min-w-0">
                  <p className="text-tok-sm text-[rgb(var(--color-text))] truncate">{cfg.label(item)}</p>
                  {cfg.subLabel(item) && (
                    <p className="text-tok-xs text-[rgb(var(--color-text-muted))] truncate">{cfg.subLabel(item)}</p>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {resolveError && (
        <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
          {resolveError}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!selected || resolving}
          onClick={handleConfirm}
          className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white disabled:opacity-50 flex items-center gap-1.5"
        >
          {resolving ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Resolving…</>
          ) : (
            <>Continue →</>
          )}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RoleTransitionInitiator — CCM §8.8
// 4-step inline form. Rendered in panel body, not a modal.
// POST /positions/role-transition on confirm.
// ---------------------------------------------------------------------------
const TRANSITION_MODES = [
  {
    key:   "role_only",
    label: "Role change only",
    desc:  "Update role classification. Current plan continues unchanged. Use when the role label changes but comp terms stay the same.",
  },
  {
    key:   "role_and_plan",
    label: "Role change + new plan assignment",
    desc:  "Supersede current plan. Assign new plan from new effective date. Use when compensation terms change with the new role.",
  },
  {
    key:   "future_dated",
    label: "Future-dated role change",
    desc:  "Schedule for start of next period. Current period unaffected.",
  },
];

function RoleTransitionInitiator({ fiscalPeriodId, wizardSessionId, onSuccess, onCancel }) {
  const [step,          setStep]          = useState(1); // 1=position 2=mode 3=configure 4=reason
  const [posSearch,     setPosSearch]     = useState("");
  const [position,      setPosition]      = useState(null);
  const [mode,          setMode]          = useState(null);
  const [roles,         setRoles]         = useState([]);
  const [plans,         setPlans]         = useState([]);
  const [newRoleId,     setNewRoleId]     = useState("");
  const [newPlanId,     setNewPlanId]     = useState("");
  const [transitionDate, setTransitionDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reasonNote,    setReasonNote]    = useState("");
  const [submitting,    setSubmitting]    = useState(false);
  const [submitError,   setSubmitError]   = useState(null);
  const [result,        setResult]        = useState(null); // { change_package_id }

  // Fetch positions for step 1
  const { data: posData, loading: posLoading } = useFetch(`${API}/positions?is_active=true&limit=500`);
  const allPositions = posData?.data ?? posData?.positions ?? [];

  const filteredPositions = posSearch
    ? allPositions.filter(p =>
        p.full_name?.toLowerCase().includes(posSearch.toLowerCase()) ||
        p.employee_id?.toLowerCase().includes(posSearch.toLowerCase())
      )
    : allPositions;

  // Fetch roles for step 3
  useEffect(() => {
    if (step !== 3) return;
    apiFetch("/roles?is_active=true&limit=200")
      .then(d => setRoles(d.data ?? d.roles ?? []))
      .catch(() => setRoles([]));
  }, [step]);

  // Fetch plans filtered by new role for step 3 (role_and_plan mode)
  useEffect(() => {
    if (step !== 3 || mode !== "role_and_plan" || !newRoleId) return;
    apiFetch(`/plans?role_id=${newRoleId}&plan_status=approved&fiscal_period_id=${fiscalPeriodId}&limit=100`)
      .then(d => setPlans(d.data ?? d.plans ?? []))
      .catch(() => setPlans([]));
  }, [step, mode, newRoleId, fiscalPeriodId]);

  async function handleSubmit() {
    if (!position || !mode || !newRoleId || !fiscalPeriodId) return;
    if (mode === "role_and_plan" && !newPlanId) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        position_id:     position.position_id,
        new_role_id:     Number(newRoleId),
        transition_mode: mode,
        transition_date: transitionDate,
        fiscal_period_id: Number(fiscalPeriodId),
      };
      if (mode === "role_and_plan") body.new_plan_id = Number(newPlanId);
      if (reasonNote.trim())        body.package_reason_note = reasonNote.trim();
      if (wizardSessionId)          body.wizard_session_id = wizardSessionId;

      const data = await apiFetch("/positions/role-transition", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      setResult({ change_package_id: data.change_package_id ?? data.data?.change_package_id });
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitForApproval() {
    if (!result?.change_package_id) return;
    try {
      await apiFetch(`/amendments/packages/${result.change_package_id}/submit`, { method: "POST" });
      onSuccess?.({ change_package_id: result.change_package_id });
    } catch (e) {
      setSubmitError(e.message);
    }
  }

  // ── Success state ──
  if (result) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
        <CheckCircle className="h-12 w-12 text-[rgb(var(--color-success))]" />
        <div>
          <p className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Change package opened.</p>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-1">
            Submit for approval when ready. CA-2 will review the intent before the entity is unlocked.
          </p>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-1 font-mono">
            {result.change_package_id}
          </p>
        </div>
        {submitError && <ErrorMsg msg={submitError} />}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-[rgb(var(--color-border))] px-4 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
          >
            Done
          </button>
          <button
            type="button"
            onClick={handleSubmitForApproval}
            className="rounded-lg bg-[rgb(var(--color-primary))] px-5 py-1.5 text-tok-sm font-medium text-white hover:opacity-90"
          >
            Submit for Approval →
          </button>
        </div>
      </div>
    );
  }

  // ── Step progress indicator ──
  const STEPS = ["Select Position", "Select Mode", "Configure", "Reason"];

  return (
    <div className="space-y-4">
      {/* Step indicator */}
      <div className="flex items-center gap-0">
        {STEPS.map((s, i) => {
          const n        = i + 1;
          const active   = step === n;
          const complete = step > n;
          return (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <div className="flex items-center gap-2">
                <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                  complete ? "bg-[rgb(var(--color-success))] text-white"
                  : active  ? "bg-[rgb(var(--color-primary))] text-white"
                  : "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]"
                }`}>
                  {complete ? "✓" : n}
                </span>
                <span className={`text-tok-xs whitespace-nowrap ${active ? "text-[rgb(var(--color-text))] font-medium" : "text-[rgb(var(--color-text-muted))]"}`}>
                  {s}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className="flex-1 h-px bg-[rgb(var(--color-border))] mx-2" />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Step 1 — Select Position ── */}
      {step === 1 && (
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] pl-8 pr-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
              placeholder="Search by name or employee ID…"
              value={posSearch}
              onChange={e => setPosSearch(e.target.value)}
            />
          </div>
          <div className="rounded-xl border border-[rgb(var(--color-border))] overflow-hidden max-h-72 overflow-y-auto">
            {posLoading ? (
              <div className="p-4 space-y-2">
                {[...Array(5)].map((_, i) => <div key={i} className="h-4 rounded bg-[rgb(var(--color-surface-muted))] animate-pulse" />)}
              </div>
            ) : filteredPositions.length === 0 ? (
              <p className="p-4 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">No positions found.</p>
            ) : (
              filteredPositions.slice(0, 50).map(p => {
                const isSelected = position?.position_id === p.position_id;
                return (
                  <button
                    key={p.position_id}
                    type="button"
                    onClick={() => setPosition(p)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-[rgb(var(--color-border))] last:border-0 transition-colors ${
                      isSelected ? "bg-[rgb(var(--color-primary))]/5" : "hover:bg-[rgb(var(--color-surface-muted))]"
                    }`}
                  >
                    <span className={`h-3.5 w-3.5 shrink-0 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]" : "border-[rgb(var(--color-text-muted))]"
                    }`}>
                      {isSelected && <span className="h-1 w-1 rounded-full bg-white" />}
                    </span>
                    <div className="min-w-0">
                      <p className="text-tok-sm text-[rgb(var(--color-text))] truncate">{p.full_name}</p>
                      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] truncate">
                        {[p.employee_id, p.role_code, p.segment_code].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {position && (
            <div className="rounded-lg border border-[rgb(var(--color-primary))]/30 bg-[rgb(var(--color-primary))]/5 px-3 py-2 text-tok-xs text-[rgb(var(--color-text))]">
              Selected: <strong>{position.full_name}</strong> · Current role: {position.role_code ?? "—"} · Segment: {position.segment_code ?? "—"}
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              disabled={!position}
              onClick={() => setStep(2)}
              className="rounded-lg bg-[rgb(var(--color-primary))] px-5 py-1.5 text-tok-sm font-medium text-white disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2 — Select Mode ── */}
      {step === 2 && (
        <div className="space-y-3">
          {TRANSITION_MODES.map(m => {
            const isSelected = mode === m.key;
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => setMode(m.key)}
                className={`w-full flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
                  isSelected
                    ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5"
                    : "border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]"
                }`}
              >
                <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${
                  isSelected ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]" : "border-[rgb(var(--color-text-muted))]"
                }`}>
                  {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                </span>
                <div>
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{m.label}</p>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">{m.desc}</p>
                </div>
              </button>
            );
          })}
          <div className="flex justify-between">
            <button type="button" onClick={() => setStep(1)}
              className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
              ← Back
            </button>
            <button type="button" disabled={!mode} onClick={() => setStep(3)}
              className="rounded-lg bg-[rgb(var(--color-primary))] px-5 py-1.5 text-tok-sm font-medium text-white disabled:opacity-50">
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3 — Configure ── */}
      {step === 3 && (
        <div className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
              New Role <span className="text-[rgb(var(--color-danger))]">*</span>
            </label>
            <select
              value={newRoleId}
              onChange={e => { setNewRoleId(e.target.value); setNewPlanId(""); }}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            >
              <option value="">Select role…</option>
              {roles.map(r => (
                <option key={r.role_id} value={r.role_id}>
                  {r.role_name} ({r.role_code})
                </option>
              ))}
            </select>
          </div>

          {mode === "role_and_plan" && (
            <div className="flex flex-col gap-1.5">
              <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                New Plan <span className="text-[rgb(var(--color-danger))]">*</span>
                <span className="font-normal ml-1">(filtered to selected role)</span>
              </label>
              <select
                value={newPlanId}
                onChange={e => setNewPlanId(e.target.value)}
                disabled={!newRoleId}
                className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none disabled:opacity-50"
              >
                <option value="">Select plan…</option>
                {plans.map(p => (
                  <option key={p.plan_id} value={p.plan_id}>
                    {p.plan_name} ({p.plan_code})
                  </option>
                ))}
              </select>
              {newRoleId && plans.length === 0 && (
                <p className="text-tok-xs text-[rgb(var(--color-warning))]">
                  No approved plans found for this role in the current period.
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
              {mode === "future_dated" ? "Target Effective Date" : "Effective Date"}{" "}
              <span className="text-[rgb(var(--color-danger))]">*</span>
            </label>
            <input
              type="date"
              value={transitionDate}
              onChange={e => setTransitionDate(e.target.value)}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            />
          </div>

          <div className="flex justify-between">
            <button type="button" onClick={() => setStep(2)}
              className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
              ← Back
            </button>
            <button
              type="button"
              disabled={!newRoleId || (mode === "role_and_plan" && !newPlanId) || !transitionDate}
              onClick={() => setStep(4)}
              className="rounded-lg bg-[rgb(var(--color-primary))] px-5 py-1.5 text-tok-sm font-medium text-white disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4 — Reason + Confirm ── */}
      {step === 4 && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 space-y-1.5 text-tok-xs">
            <p className="font-semibold text-[rgb(var(--color-text))] mb-2">Change Summary</p>
            <p className="text-[rgb(var(--color-text-muted))]">
              Position: <span className="text-[rgb(var(--color-text))]">{position?.full_name}</span>
            </p>
            <p className="text-[rgb(var(--color-text-muted))]">
              Mode: <span className="text-[rgb(var(--color-text))]">{TRANSITION_MODES.find(m => m.key === mode)?.label}</span>
            </p>
            <p className="text-[rgb(var(--color-text-muted))]">
              New role ID: <span className="text-[rgb(var(--color-text))] font-mono">{newRoleId}</span>
            </p>
            {mode === "role_and_plan" && (
              <p className="text-[rgb(var(--color-text-muted))]">
                New plan ID: <span className="text-[rgb(var(--color-text))] font-mono">{newPlanId}</span>
              </p>
            )}
            <p className="text-[rgb(var(--color-text-muted))]">
              Effective date: <span className="text-[rgb(var(--color-text))]">{transitionDate}</span>
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
              Note <span className="text-tok-xs font-normal">(optional)</span>
            </label>
            <textarea
              value={reasonNote}
              onChange={e => setReasonNote(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Describe the reason for this role transition…"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none resize-none"
            />
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] text-right">{reasonNote.length}/500</p>
          </div>

          {submitError && <ErrorMsg msg={submitError} />}

          <div className="flex justify-between">
            <button type="button" onClick={() => setStep(3)}
              className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
              ← Back
            </button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onCancel}
                className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-5 py-1.5 text-tok-sm font-medium text-white disabled:opacity-50"
              >
                {submitting
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening package…</>
                  : <>Begin Change Package <ArrowRight className="h-4 w-4" /></>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AmendmentTypeSelector + entity selector flow (CCM §8.7)
// Rendered via createPortal at z-[9999].
// On "Role Transition" → calls onRoleTransition() to render initiator inline.
// On other types → shows entity selector pre-step → then AmendmentBeginModal.
// ---------------------------------------------------------------------------
const AMENDMENT_OPTIONS = [
  { key: "role_transition",       label: "Role Transition",         desc: "Change a rep's role and optionally their plan", entityType: null },
  { key: "quota_correction",      label: "Quota Correction",        desc: "Correct quota for one or more positions",       entityType: "quota" },
  { key: "credit_correction",     label: "Credit Correction",       desc: "Correct a credit entry",                       entityType: "credit" },
  { key: "rate_table_fix",        label: "Rate Table Fix",          desc: "Adjust rate table bands or payout percentages", entityType: "rate_table" },
  { key: "plan_component_update", label: "Plan Component Update",   desc: "Adjust weights, OTE, or component structure",   entityType: "plan_component" },
  { key: "other",                 label: "Other Amendment",         desc: "Select entity type manually",                   entityType: null },
];

function AmendmentTypeSelector({ onClose, onRoleTransition, fiscalPeriodId, wizardSessionId, period }) {
  const [selected,       setSelected]       = useState(null);
  // "type_select" | "entity_select" | "begin_modal"
  const [phase,          setPhase]          = useState("type_select");
  const [entityId,       setEntityId]       = useState(null);
  const [entityType,     setEntityType]     = useState(null);

  const selectedOption = AMENDMENT_OPTIONS.find(o => o.key === selected);

  function handleProceed() {
    if (!selected) return;
    if (selected === "role_transition") {
      onClose();
      onRoleTransition();
      return;
    }
    if (selected === "other") {
      // "Other" has no entity selector — open modal with null entityId
      // Modal will surface the validation error clearly
      setEntityType("quota"); // default to quota as a hint; user can re-select from modal if needed
      setPhase("begin_modal");
      return;
    }
    // All others need entity selection first
    setEntityType(selectedOption.entityType);
    setPhase("entity_select");
  }

  function handleEntitySelected({ entityId: eid, entityType: etype }) {
    setEntityId(eid);
    setEntityType(etype);
    setPhase("begin_modal");
  }

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="bg-[rgb(var(--color-surface))] rounded-2xl border border-[rgb(var(--color-border))] shadow-2xl w-[560px] max-h-[85vh] overflow-auto flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
          <p className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
            {phase === "type_select"   && "New Change"}
            {phase === "entity_select" && (
              entityType === "quota" || entityType === "credit"
                ? "Select position"
                : `Select ${entityType?.replace(/_/g, " ")}`
            )}
            {phase === "begin_modal"   && "Begin Amendment"}
          </p>
          <button type="button" onClick={onClose}
            className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 p-5 overflow-auto">
          {/* Phase 1 — type selection */}
          {phase === "type_select" && (
            <div className="space-y-2">
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mb-3">What would you like to change?</p>
              {AMENDMENT_OPTIONS.map(opt => {
                const isSelected = selected === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSelected(opt.key)}
                    className={`w-full flex items-start gap-3 rounded-xl border p-3 text-left cursor-pointer transition-colors ${
                      isSelected
                        ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5"
                        : "border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]"
                    }`}
                  >
                    <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]"
                        : "border-[rgb(var(--color-text-muted))] bg-transparent"
                    }`}>
                      {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </span>
                    <div>
                      <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{opt.label}</p>
                      <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{opt.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Phase 2 — entity selector pre-step (fixes Bug 2) */}
          {phase === "entity_select" && entityType && (
            <EntitySelector
              entityType={entityType}
              fiscalPeriodId={fiscalPeriodId}
              onSelect={handleEntitySelected}
              onCancel={() => setPhase("type_select")}
            />
          )}
        </div>

        {/* Footer — only for type_select phase */}
        {phase === "type_select" && (
          <div className="flex justify-end gap-2 border-t border-[rgb(var(--color-border))] px-5 py-3 shrink-0">
            <button type="button" onClick={onClose}
              className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
              Cancel
            </button>
            <button type="button" onClick={handleProceed} disabled={!selected}
              className="rounded-lg bg-[rgb(var(--color-primary))] px-4 py-1.5 text-tok-sm font-medium text-white disabled:opacity-50">
              Continue →
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // AmendmentBeginModal is rendered outside the selector modal so it sits at z-[9999] via its own portal-like container.
  // The selector modal is dismissed first before opening begin modal to avoid stacking.
  if (phase === "begin_modal") {
    return createPortal(
      <>
        <AmendmentBeginModal
          open
          entityType={entityType}
          entityId={entityId}
          fiscalPeriodId={fiscalPeriodId}
          wizardSessionId={wizardSessionId}
          periodLabel={period?.period_label ?? period?.period_code ?? null}
          periodStatus={period?.period_status ?? null}
          onBegin={(amendmentId) => {
            onClose();
          }}
          onCancel={() => setPhase(entityType && entityType !== "quota" ? "entity_select" : "type_select")}
        />
      </>,
      document.body
    );
  }

  return createPortal(modal, document.body);
}

// ---------------------------------------------------------------------------
// Root — ChangeManagementPanel
// ---------------------------------------------------------------------------
const TABS = [
  { key: "pending",   label: "Pending Approval" },
  { key: "open",      label: "Open" },
  { key: "committed", label: "Committed" },
  { key: "history",   label: "History" },
];

export default function ChangeManagementPanel({ period, fiscalPeriodId, wizardSessionId }) {
  const { user } = useAuth();
  // S622: AuthContext exposes user_account_id (not user_id) from /api/v1/auth/me
  const currentUserId = user?.user_account_id ?? null;

  const [tab,                 setTab]                 = useState("pending");
  const [showNewChange,       setShowNewChange]        = useState(false);
  const [showRoleTransition,  setShowRoleTransition]   = useState(false);
  const [pendingCount,        setPendingCount]         = useState(null);

  // Badge count fetch for Pending tab label
  const { data: pendingData, refresh: refreshPending } = useFetch(
    `${API}/amendments?amendment_status=pending_approval${fiscalPeriodId ? `&fiscal_period_id=${fiscalPeriodId}` : ""}&limit=50`
  );
  useEffect(() => {
    setPendingCount(pendingData?.amendments?.length ?? null);
  }, [pendingData]);

  function handleRoleTransition() {
    setShowNewChange(false);
    setShowRoleTransition(true);
  }

  function handleRoleTransitionSuccess({ change_package_id }) {
    setShowRoleTransition(false);
    refreshPending();
    // Switch to open tab so user sees the newly opened package
    setTab("open");
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <GitPullRequest className="h-5 w-5 text-[rgb(var(--color-primary))]" />
            <h1 className="text-tok-lg font-semibold text-[rgb(var(--color-text))]">Change Management</h1>
          </div>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Governed, auditable workflow for mid-period compensation changes. Four-eyes approval at every gate.
          </p>
        </div>
        {!showRoleTransition && (
          <button
            type="button"
            onClick={() => setShowNewChange(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:opacity-90 shrink-0"
          >
            <Plus className="h-4 w-4" /> New Change
          </button>
        )}
      </div>

      {/* Role Transition Initiator — inline in panel, replaces tab content (fixes Bug 1) */}
      {showRoleTransition ? (
        <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5">
          {/* Compact header bar */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-[rgb(var(--color-border))]">
            <div className="flex items-center gap-3">
              <div>
                <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-warning))]">
                  Governed Change
                </p>
                <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Role Transition</h2>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowRoleTransition(false)}
              className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <RoleTransitionInitiator
            fiscalPeriodId={fiscalPeriodId}
            wizardSessionId={wizardSessionId}
            onSuccess={handleRoleTransitionSuccess}
            onCancel={() => setShowRoleTransition(false)}
          />
        </div>
      ) : (
        <>
          {/* Tab bar */}
          <div className="flex border-b border-[rgb(var(--color-border))] mb-6">
            {TABS.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`relative px-4 py-2.5 text-tok-sm font-medium border-b-2 transition-colors ${
                  tab === t.key
                    ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]"
                    : "border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                }`}
              >
                {t.label}
                {t.key === "pending" && pendingCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-[rgb(var(--color-warning))] text-white text-[10px] font-bold h-4 min-w-[1rem] px-1">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {tab === "pending" && (
            <PendingApprovalTab
              fiscalPeriodId={fiscalPeriodId}
              currentUserId={currentUserId}
              onRefresh={refreshPending}
            />
          )}
          {tab === "open" && (
            <OpenTab
              fiscalPeriodId={fiscalPeriodId}
              currentUserId={currentUserId}
              onRefresh={refreshPending}
            />
          )}
          {tab === "committed" && (
            <CommittedTab
              fiscalPeriodId={fiscalPeriodId}
              currentUserId={currentUserId}
              wizardSessionId={wizardSessionId}
              onRefresh={refreshPending}
            />
          )}
          {tab === "history" && (
            <HistoryTab fiscalPeriodId={fiscalPeriodId} />
          )}
        </>
      )}

      {/* New Change modal — createPortal at z-[9999] */}
      {showNewChange && (
        <AmendmentTypeSelector
          onClose={() => setShowNewChange(false)}
          onRoleTransition={handleRoleTransition}
          fiscalPeriodId={fiscalPeriodId}
          wizardSessionId={wizardSessionId}
          period={period}
        />
      )}
    </div>
  );
}
